/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

/**
 * Component that creates a 2D engines Widget
 * required params:
 *  - position
 *  - width
 *  - height

 *
 */

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    TextArea,
    SwitchButtons,
    OverlayPanel,
    Button,
    BackgroundPanel,
} from "vr-react-components";
import { setOpenedModal } from "../../store/slice/roomSlices/jobRoomSlice";

const THREE = window.THREE;

function JobLogModal({ jobLog, ...otherAttributes }) {
    const canModify = useSelector((state) => state.private.device.canModify);

    const [worldPosition, setWorldPosition] = useState({ x: 0, y: 0, z: 0 });
    const [selectedButton, setSelectedButton] = useState("Current");
    const root = React.useRef();
    const dispatch = useDispatch();
    useEffect(() => {
        const parent = root.current.parentNode;
        let worldPos = new THREE.Vector3(0, 1.65, -0.9);
        const localPos = parent.object3D.worldToLocal(worldPos);
        if (
            localPos.x !== worldPosition.x ||
            localPos.y !== worldPosition.y ||
            localPos.z !== worldPosition.z
        )
            setWorldPosition(localPos);
    }, [worldPosition]);

    const openPanelDimension = 1.15;
    if (!jobLog.previous || jobLog.previous === "")
        jobLog.previous = "There are no successful instance of this job";

    return (
        <>
            <BackgroundPanel
                ref={root}
                position={`${worldPosition.x} ${worldPosition.y} ${worldPosition.z}`}
                width={openPanelDimension}
                height={openPanelDimension}
                opacity={1}
                title="Job log details"
                textOffset={{ x: openPanelDimension / 20, y: openPanelDimension / 20 }}
                backgroundColor="#363636"
                {...otherAttributes}
            >
                <SwitchButtons
                    height={openPanelDimension * 0.05}
                    width={openPanelDimension * 0.9}
                    aframeClasses={canModify ? "collidable" : ""}
                    options={[
                        {
                            element: "Current",
                            onClickHandler: () => {
                                setSelectedButton("Current");
                            },
                        },
                        {
                            element: "Previous successful",
                            onClickHandler: () => {
                                setSelectedButton("Previous successful");
                            },
                        },
                    ]}
                    textSizeDecimal={50 / openPanelDimension}
                    selected={selectedButton}
                    position={`0 ${openPanelDimension * 0.3 + openPanelDimension * 0.035
                        } 0.001`}
                />
                <TextArea
                    text={selectedButton === "Current" ? jobLog?.current : jobLog?.previous}
                    width={openPanelDimension * 0.9}
                    height={openPanelDimension * 0.6}
                    aframeClasses={canModify ? "collidable" : ""}
                    position={`0 0 0.001`}
                    font="sourcecodepro"
                />
                <Button
                    text="Close"
                    width={openPanelDimension * 0.4}
                    height={openPanelDimension * 0.1}
                    onClickHandler={() => {
                        dispatch(setOpenedModal(null))
                    }}
                    aframeClasses={canModify ? "collidable" : ""}
                    position={`${openPanelDimension / 2 -
                        openPanelDimension * 0.05 -
                        openPanelDimension * 0.2
                        } ${-openPanelDimension / 2 +
                        openPanelDimension * 0.05 +
                        openPanelDimension * 0.05
                        } 0.001`}
                    type={Button.constants.primary}
                    textSizeDecimal={40 / openPanelDimension}
                />
            </BackgroundPanel>
            <OverlayPanel
                aframeClasses={canModify ? "collidable" : ""}
                position={`0 0 ${worldPosition.z * 0.8}`}
                width={openPanelDimension * 10}
                height={openPanelDimension * 10}
                onClickHandler={() => {
                    dispatch(setOpenedModal(null))
                }}
            />
        </>
    );
}

export default JobLogModal;
